import { AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-hot-toast';
import { RootState } from '@/app/appStore';
import {
  addFrame,
  deleteFrame,
  setPending,
  syncFrame,
  transformFrameIntoImage,
  updateImageSize,
} from '@/entities/collection';
import { buildFrame } from '@/entities/collection/model/helpers';
import { CaptureImageResponse, collectionApi } from '@/shared/api';

const captureImage: AsyncThunkPayloadCreator<CaptureImageResponse> = async (
  _,
  { dispatch, getState }
) => {
  const state = getState() as RootState;
  const collectionId = state.collection.collectionId;
  const zoneId = state.collection.zoneId;

  if (!collectionId) {
    throw Error("Collection ID doesn't exist");
  }
  if (!zoneId) {
    throw Error("Zone ID doesn't exist");
  }

  return dispatch(
    collectionApi.endpoints.captureImage.initiate({
      collection_id: collectionId,
      zone_id: zoneId,
    })
  ).unwrap();
};

export const captureBackground = createAsyncThunk(
  'collection/captureBackground',
  captureImage
);

export const captureFrame = createAsyncThunk(
  'collection/captureFrame',
  async (_, { dispatch, getState }) => {
    dispatch(setPending('frame'));
    const state = getState() as RootState;
    const collectionId = state.collection.collectionId;
    const zoneId = state.collection.zoneId;
    const isFrameReady =
      state.collection.skuList.length ===
      Object.keys(state.collection.skuToPoint).length;

    if (!collectionId) {
      dispatch(setPending(null));
      throw Error("Collection ID doesn't exist");
    }
    if (!zoneId) {
      dispatch(setPending(null));
      throw Error("Zone ID doesn't exist");
    }

    if (state.collection.mode === 'multi' && !isFrameReady) {
      toast.error(
        'Not all SKU location points are placed. Ensure that every SKU location point is positioned on the canvas before capturing a frame.'
      );
      dispatch(setPending(null));
      return;
    }

    if (state.collection.skuList.length === 0) {
      toast.error('Add SKU to the list');
      dispatch(setPending(null));
      return;
    }

    const topLeft = state.canvas.bgRectangle?.topLeft;
    const bottomRight = state.canvas.bgRectangle?.bottomRight;

    if (
      !state.collection.backgroundImageId ||
      !state.collection.backgroundImage ||
      !topLeft ||
      !bottomRight
    ) {
      toast.error('Background is not captured');
      dispatch(setPending(null));
      return;
    }

    const {
      result: { png_image_path, webp_image_path, width, height },
    } = await dispatch(
      collectionApi.endpoints.captureImage.initiate({
        collection_id: collectionId,
        zone_id: zoneId,
      })
    ).unwrap();

    dispatch(updateImageSize({ width, height }));

    const newFrameData = buildFrame(
      state.collection,
      png_image_path,
      webp_image_path,
      state.collection.backgroundImageId,
      topLeft,
      bottomRight
    );

    dispatch(addFrame(newFrameData));
    dispatch(setPending(null));
    toast.success('Frame captured');

    const { canvasWidth, canvasHeight } = state.canvas.dimensions;
    const dimensions = {
      canvasWidth,
      canvasHeight,
      videoWidth: width,
      videoHeight: height,
    };

    const frame = await dispatch(
      collectionApi.endpoints.createImage.initiate(
        transformFrameIntoImage(collectionId, newFrameData, dimensions)
      )
    ).unwrap();

    dispatch(syncFrame(frame.result));

    return newFrameData;
  }
);

export const deleteFrameImage = createAsyncThunk(
  'collection/deleteFrame',
  (_, { dispatch, getState }) => {
    const state = getState() as RootState;

    if (!state.collection.collectionId || !state.collection.framesList.length)
      return;

    const index =
      state.collection.selectedFrameIndex < 0
        ? state.collection.framesList.length - 1
        : state.collection.selectedFrameIndex;
    const currentFrame = state.collection.framesList[index];

    dispatch(deleteFrame());

    toast.success('Frame deleted successfully');

    if (!currentFrame.id) {
      toast.error('Frame is not synced with backend');
      return;
    }

    return dispatch(
      collectionApi.endpoints.deleteImage.initiate({
        collection_id: state.collection.collectionId,
        image_id: currentFrame.id,
      })
    );
  }
);

export const deleteFramePoint = createAsyncThunk(
  'collection/deleteFramePoint',
  (_, { dispatch, getState }) => {
    const state = getState() as RootState;

    if (!state.collection.collectionId || !state.collection.framesList.length)
      return;

    const index =
      state.collection.selectedFrameIndex < 0
        ? state.collection.framesList.length - 1
        : state.collection.selectedFrameIndex;
    const currentFrame = state.collection.framesList[index];

    dispatch(deleteFrame());

    if (!currentFrame.id) {
      toast.error('Frame is not synced with backend');
      return;
    }

    return dispatch(
      collectionApi.endpoints.deleteImage.initiate({
        collection_id: state.collection.collectionId,
        image_id: currentFrame.id,
      })
    );
  }
);
